import { notificationApiClient } from './api';

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  category?: string;
  createdAt: string;
  read: boolean;
  metadata?: Record<string, unknown>;
}

export interface NotificationListResponse {
  notifications: NotificationItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface NotificationPreference {
  type: string;
  channel: string;
  enabled: boolean;
  mandatory?: boolean;
}

export interface UpdatePreferencePayload {
  type: string;
  channel: string;
  enabled: boolean;
}

const toNumber = (value: unknown, fallback = 0): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const getString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return fallback;
  return String(value);
};

type NotificationRecord = Record<string, unknown>;

const asNotificationRecord = (value: unknown): NotificationRecord =>
  value && typeof value === 'object' ? (value as NotificationRecord) : {};

const normalizeNotification = (input: unknown): NotificationItem => {
  const source = asNotificationRecord(input);
  const category = getString(source.category ?? source.type);

  return ({
  id: getString(
    source.id ?? source.notificationId,
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  ),
  title: getString(source.title ?? source.heading ?? 'Notification'),
  body: getString(source.body ?? source.message ?? ''),
  category: category || undefined,
  createdAt: getString(source.createdAt ?? source.timestamp ?? new Date().toISOString()),
  read: Boolean(source.read ?? source.isRead ?? false),
  metadata:
    typeof source.metadata === 'object' && source.metadata !== null
      ? (source.metadata as Record<string, unknown>)
      : undefined
  });
};

const normalizePagination = (input: unknown) => {
  const source = asNotificationRecord(input);
  return {
    page: toNumber(source.page ?? 1, 1),
    limit: toNumber(source.limit ?? source.size ?? 20, 20),
    total: toNumber(source.total ?? 0, 0),
    totalPages: toNumber(source.totalPages ?? 1, 1)
  };
};

const normalizePreferences = (input: unknown): NotificationPreference[] => {
  if (!Array.isArray(input)) {
    return [];
  }

  return input.map((item) => {
    const source = asNotificationRecord(item);
    return {
      type: getString(source.type ?? 'UNKNOWN'),
      channel: getString(source.channel ?? 'IN_APP'),
      enabled: Boolean(source.enabled ?? false),
      mandatory: source.mandatory === true
    };
  });
};

interface NotificationListParams {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
}

const buildListQuery = (
  params?: { page?: number; limit?: number; unreadOnly?: boolean }
): URLSearchParams => {
  const query = new URLSearchParams();
  if (params?.page) {
    query.append('page', Math.max(1, params.page).toString());
  }
  if (params?.limit) {
    query.append('limit', Math.max(1, params.limit).toString());
  }
  if (params?.unreadOnly !== undefined) {
    query.append('unreadOnly', String(params.unreadOnly));
  }
  return query;
};

export const notificationsApi = {
  async list(params: NotificationListParams): Promise<NotificationListResponse> {
    const query = buildListQuery(params);
    const endpoint = query.toString() ? `/in-app?${query.toString()}` : '/in-app';
    const raw = await notificationApiClient.get<unknown>(endpoint, true);
    const source = asNotificationRecord(raw);
    const notificationsSource = Array.isArray(source.notifications)
      ? source.notifications
      : Array.isArray(source.data)
        ? source.data
        : raw;
    const notifications = Array.isArray(notificationsSource)
      ? notificationsSource.map(normalizeNotification)
      : Array.isArray(raw)
        ? raw.map(normalizeNotification)
        : [];
    const paginationSource = source.pagination ?? source.meta ?? {
      page: params?.page ?? 1,
      limit: params?.limit ?? notifications.length,
      total: notifications.length,
      totalPages: notifications.length ? 1 : 0
    };

    return {
      notifications,
      pagination: normalizePagination(paginationSource)
    };
  },

  async unreadCount(): Promise<number> {
    const raw = await notificationApiClient.get<unknown>('/in-app/unread-count', true);
    const source = asNotificationRecord(raw);
    if (typeof source.count === 'number') return source.count;
    if (typeof source.unread === 'number') return source.unread;
    if (typeof raw === 'number') return raw;
    return 0;
  },

  async markRead(notificationId: string): Promise<void> {
    await notificationApiClient.post(`/in-app/${encodeURIComponent(notificationId)}/read`, {}, true);
  },

  async markAllRead(): Promise<void> {
    await notificationApiClient.post('/in-app/read-all', {}, true);
  },

  async getPreferences(): Promise<NotificationPreference[]> {
    const raw = await notificationApiClient.get<unknown>('/preferences', true);
    const source = asNotificationRecord(raw);
    if (Array.isArray(source.preferences)) return normalizePreferences(source.preferences);
    if (Array.isArray(raw)) return normalizePreferences(raw);
    return normalizePreferences(source.data);
  },

  async updatePreferences(preferences: UpdatePreferencePayload[]): Promise<NotificationPreference[]> {
    const payload = { preferences: preferences.map(pref => ({
      type: pref.type,
      channel: pref.channel,
      enabled: pref.enabled
    })) };
    const raw = await notificationApiClient.put<unknown>('/preferences', payload, true);
    const source = asNotificationRecord(raw);
    if (Array.isArray(source.preferences)) return normalizePreferences(source.preferences);
    if (Array.isArray(raw)) return normalizePreferences(raw);
    return normalizePreferences(source.data);
  }
};
